import { Profile } from '../types';

export interface SmartMatchWeights {
  rating?: number; // 0-100 weight portion
  price?: number;
  availability?: number;
  recency?: number;
}

const DEFAULT_WEIGHTS: Required<SmartMatchWeights> = {
  rating: 45,
  price: 25,
  availability: 20,
  recency: 10
};

/**
 * Calcule un score SmartMatch HEG pour un tuteur.
 * Pondérations inspirées d'un mix conversion/valeur perçue.
 */
export function scoreTutor(tutor: Profile, cohort: Profile[], weights: SmartMatchWeights = {}): number {
  const w = { ...DEFAULT_WEIGHTS, ...weights };
  const totalW = w.rating + w.price + w.availability + w.recency;
  const normalize = (val: number) => (totalW === 0 ? 0 : (val / totalW) * 100);

  const notes = tutor.notes || [];
  const avgRating = notes.length > 0
    ? notes.reduce((s, n) => s + (n.note || 0), 0) / notes.length
    : 3; // neutre si pas de note
  const ratingScore = normalize((avgRating / 5) * w.rating);

  const tariffs = cohort.map(t => t.tarif || 0);
  const minTarif = Math.min(...tariffs);
  const maxTarif = Math.max(...tariffs);
  let priceScore = normalize(w.price * 0.5);
  if (maxTarif !== minTarif) {
    const normalized = (maxTarif - (tutor.tarif || minTarif)) / (maxTarif - minTarif);
    priceScore = normalize(normalized * w.price);
  }

  const maxDispos = Math.max(...cohort.map(t => (t.dispos ? t.dispos.length : 0), 0));
  let availabilityScore = normalize(w.availability * 0.5);
  if (maxDispos > 0) {
    const count = tutor.dispos ? tutor.dispos.length : 0;
    availabilityScore = normalize((count / maxDispos) * w.availability);
  }

  const created = new Date(tutor.created_at).getTime();
  const now = Date.now();
  const ninetyDays = 90 * 24 * 60 * 60 * 1000;
  const recency = Math.max(0, Math.min(1, (now - created < ninetyDays ? 1 - (now - created) / ninetyDays : 0)));
  const recencyScore = normalize(recency * w.recency);

  // Jitter léger et stable par id
  const hash = Array.from(tutor.id).reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 1000, 7);
  const jitter = (hash % 5) * 0.1; // max +0.4

  return ratingScore + priceScore + availabilityScore + recencyScore + jitter;
}

/** Trie une liste de tuteurs selon SmartMatch HEG. */
export function sortTutorsSmartMatch(tutors: Profile[], weights?: SmartMatchWeights): Profile[] {
  if (!tutors || tutors.length <= 1) return tutors;
  return [...tutors].sort((a, b) => scoreTutor(b, tutors, weights) - scoreTutor(a, tutors, weights));
}



